import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../../features/langSlice';
import { useAnswersMutation, useLazyAnswersWebQuery } from '../../features/quizApiSlice';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getSubsActions } from '../../app/api/useDataManage/subscriptionApis';
import { useTranslation } from 'react-i18next';
import { isGermanLanguage } from '../../utils/language';
import { getPersonalFormulaReadyText } from '../../utils/product-copy';

// Constants imports
import {
  YourFocusAreasDesc,
  ProductSuggestionDoses,
  ProductSuggestionEyebrow,
  ProductSuggestionImprovementBadge,
  ProductSuggestionIngredientsBadge,
  StartNowButton,
  VeganVCP,
  Month,
  EveryThreeMonths,
} from '../../Constants';

// Asset imports
import WhatsappImg from '../../assets/here1.webp';

// Component imports
import MobileSubscriptionCardV2 from '../MobileSubscriptionCardV2';
import AddIngredientModal from './components/AddIngredientModal';
import Carousel from './components/Carousel';
import FocusAreaAccordion from './components/FocusAreaAccordion';
import Loader from '../elements/Loader';
import FormulaLoader from './components/FormulaLoader';
import PdpTestimonials from './components/PdpTestimonials';
import IngredientsModal from './components/IngredientsModal';
import EditFormulaModal from './components/EditFormulaModal';
import ProductHighlightPill from './components/ProductHighlightPill';
import ProductPageSections from './components/ProductPageSections';

// Hooks
import useProduct from '../../hooks/useProduct';
import useOmega3Selection from '../../hooks/useOmega3Selection';
import Omega3ProductSelector from './components/Omega3ProductSelector';
import { getOmega3CheckoutTotal, getOmega3PlanAmount } from '../../utils/omega3ProductSuggestion';
import useMeta from '../../hooks/useMeta';
import { calculateIngredientPrice, withStaticDescriptions } from '../../utils/productSuggestions';
import Header from './product/Header';
import MobileSubscriptionCardV4 from '../MobileSubscriptionCardV4';
import ReviewsOverlay from '../Reviews';
import { buildMainPurposeSubline } from '../../utils/main-purpose-subline';
import {
  DEFAULT_FOCUS_COLLAPSED_COUNT,
  MAX_PRIORITY_FOCUS_COUNT,
  orderFocusAreasByPriority,
} from '../../utils/focus-area-priority';
import { orderIngredientsByFocusPriority } from '../../utils/ingredientFocusPriority';
import DiscountBanner from '../Home/DiscountBanner';
import {
  getCachedAnswersWebResult,
  setCachedAnswersWebResult,
} from '../../utils/answersWebCache';
import useReviewsIoWidgetStats from '../../hooks/use-reviews-io-widget-stats';

// Constants
const NON_EU_COUNTRIES = ['England', 'Scotland', 'Wales', 'Northern Ireland', 'Norway', 'Switzerland'];
const OMEGA3_BILLING_LABELS = {
  '3 months': 'Omega3_Selection_EveryMonth',
  '6 months': 'Omega3_Selection_EveryThreeMonths',
  '30 days': 'Omega3_Selection_OneOff30',
  '90 days': 'Omega3_Selection_OneOff90',
};

const ProductSuggestion = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const retakeNavigationStartedRef = useRef(false);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const selectedLang = useSelector(selectCurrentLanguage);
  const { subs } = useSelector((state) => state.subs);
  const { user: reduxUser } = useSelector((state) => state.user);

  // Custom hook for product data and logic
  const {
    onInputChange,
    removeIngredientFromBin,
    addIngredient,
    removeIngredient,
    onSubmitData: submitProductData,
    onRemoveQuantity,
    onAddQuantity,
    onSelectPlan,
    preloadCheckoutSessions,
    focusAreaIcons,
    allIngredients,
    allIngredientPrices,
    allProductPrices,
    onSelectOneOff,
    lang,
    setLang,
    data,
    setData,
    focusArea,
    setFocusArea,
    ingredients,
    setIngredients,
    quizId,
    setQuizId,
    user,
    setUser,
    hasFormulaChanged,
    error,
    prices,
    setPrices,
    setPricesData,
    totals,
    setTotals,
    continent,
    setContinent,
    paymentLink,
    setPaymentLink,
    isCheckoutLoading,
    subscribtion,
    setSubscription,
    addIngredientsModal,
    setAddIngredientsModal,
    newSubs,
    setNewSubs,
    ONE_OFF_SUB,
    THREE_MONTHS_ONE_OFF_SUB,
    allIngredientPriceLoading,
    amountMatches,
    getTargetAmount,
    hasPaymentLink,
    resetFormula,
  } = useProduct({ autoPreloadCheckoutSessions: false });

  // State
  const reviewStats = useReviewsIoWidgetStats();
  const [omega3Eligibility, setOmega3Eligibility] = useState({ quizId: 0, canSelect: undefined });
  const canSelectOmega3 =
    omega3Eligibility.quizId === Number(quizId) ? omega3Eligibility.canSelect : undefined;
  const omega3 = useOmega3Selection(quizId, canSelectOmega3);
  const checkoutOverrides = useMemo(
    () => omega3.isSelected ? { includeOmega3: true, checkoutContext: 'product_suggestion' } : {},
    [omega3.isSelected],
  );
  const handleCheckout = (payLink) => submitProductData(payLink, checkoutOverrides);
  
  const omega3Amount = getOmega3PlanAmount(subscribtion?.duration);
  const omega3Price = new Intl.NumberFormat(selectedLang || 'en', {
    style: 'currency',
    currency: 'EUR',
  }).format(omega3Amount);
  const omega3PriceLabel = `${omega3Price} ${t(OMEGA3_BILLING_LABELS[subscribtion?.duration] || 'Omega3_Selection_EveryMonth')}`;
  
  const omega3Option = canSelectOmega3 === true
    ? {
        isSelected: omega3.isSelected,
        hasAdded: omega3.hasAdded,
        onChange: omega3.handleChange,
        priceLabel: omega3PriceLabel,
      }
    : undefined;

  const handleResetFormula = () => {
    resetFormula();
    omega3.handleChange(false);
  };
  const [isPastMobileSubs, setIsPastMobileSubs] = useState(false);
  const [ingredientsModal, setIngredientsModal] = useState(false);
  const [scrolledPastBanner, setScrolledPastBanner] = useState(false);
  const [editIngredientsModal, setEditIngredientsModal] = useState(false);
  const [personalSignalsCount, setPersonalSignalsCount] = useState(0);
  const [specialConditionsCount, setSpecialConditionsCount] = useState(0);
  const [medicationsCount, setMedicationsCount] = useState(0);
  const [mainPurpose, setMainPurpose] = useState('');
  const [symptoms, setSymptoms] = useState([]);
  const [priorityFocus, setPriorityFocus] = useState([]);
  const [mainPurposeContext, setMainPurposeContext] = useState(null);
  const [aiFromCache, setAiFromCache] = useState(false);
  const [showFormulaLoader, setShowFormulaLoader] = useState(
    !!state?.freshFromQuiz || !!state?.returningUser,
  );
  const [dataResolved, setDataResolved] = useState(false);
  const [isFetchingAnswers, setIsFetchingAnswers] = useState(false);
  const freshFromQuizRef = React.useRef(!!state?.freshFromQuiz);
  const returningUserRef = React.useRef(!!state?.returningUser);
  // Name available before React state updates — used by FormulaLoader
  const freshNameRef = React.useRef(state?.quizAnswers?.[0]?.name?.trim().split(' ')[0] || null);
  const fetchedNameRef = React.useRef(null);
  // Capture fresh quiz nav state at render time (before effects) — safe against router state clearing
  const freshQuizStateRef = React.useRef(state?.freshFromQuiz ? state : null);
  const freshQuizFiredRef = React.useRef(false);
  const initialLoadDoneRef = React.useRef(false);
  const [offer, setOffer] = useState(searchParams.get('offer'));
  const stateQuizId = Number(state?.quiz_id || 0);
  const mainPurposeSubline = useMemo(
    () => buildMainPurposeSubline(t, mainPurpose, mainPurposeContext),
    [mainPurpose, mainPurposeContext, t],
  );
  const orderedFocusArea = useMemo(
    () => orderFocusAreasByPriority(focusArea, priorityFocus),
    [focusArea, priorityFocus],
  );
  const safeFocusArea = Array.isArray(focusArea) ? focusArea : [];
  const safeData = Array.isArray(data) ? data : [];
  const safeIngredients = Array.isArray(ingredients) ? ingredients : [];
  const priorityFocusCollapsedCount = useMemo(() => {
    if (!Array.isArray(priorityFocus) || priorityFocus.length === 0) return DEFAULT_FOCUS_COLLAPSED_COUNT;
    return Math.min(priorityFocus.length, MAX_PRIORITY_FOCUS_COUNT);
  }, [priorityFocus]);
  const totalIngredientsCount = data?.length || 0;
  // "Edit your formula" on the carousel opens the edit modal directly (not the read-only formula popup).
  const openIngredientsModal = useCallback(() => setIngredientsModal(true), []);
  const editOpenedFromCarouselRef = useRef(false);
  const openEditFormulaFromCarousel = useCallback(() => {
    editOpenedFromCarouselRef.current = true;
    setEditIngredientsModal(true);
  }, []);
  // Inputs for the product-suggestion carousel slides (offer, purpose, price comparison, focus areas).
  const carouselSuggestion = useMemo(
    () => {
      if (!dataResolved || !data?.length) return null;

      return {
        planDuration: subscribtion?.duration,
        mainPurpose,
        symptoms,
        ingredients: data,
        focusAreas: orderedFocusArea,
        onEditFormula: openEditFormulaFromCarousel,
        onViewFormula: openIngredientsModal,
      };
    },
    [
      dataResolved,
      data,
      subscribtion?.duration,
      mainPurpose,
      symptoms,
      orderedFocusArea,
      openEditFormulaFromCarousel,
      openIngredientsModal,
    ],
  );
  // Refs
  const boxRef = useRef(null);
  const mobileSubsRef = useRef();
  const sectionRefMobile = useRef(null);
  const leftContainerRef = useRef(null);
  const rightScrollRef = useRef(null);
  const mainContainerRef = useRef(null);
  const hasInitializedPlanRef = useRef(false);
  const bannerRef = useRef(null);
  const currentLangRequestRef = useRef(null);

  // Queries
  const [submitAnswers, { isLoading: isAnswersSubmitLoading }] = useAnswersMutation();
  const [loadAnswersWeb, { isFetching: isAnswersWebLoading }] = useLazyAnswersWebQuery();

  // Memoized values
  const isResultLoading = useMemo(
    () => isAnswersSubmitLoading || isAnswersWebLoading || isFetchingAnswers || allIngredientPriceLoading,
    [isAnswersSubmitLoading, isAnswersWebLoading, isFetchingAnswers, allIngredientPriceLoading],
  );

  const scrollToSectionEndMobile = useCallback(() => {
    if (sectionRefMobile.current) {
      sectionRefMobile.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, []);

  const isBannerVisible = useMemo(() => isPastMobileSubs, [isPastMobileSubs]);

  const applyAnswersWebResult = useCallback(
    (result, options = {}) => {
      const { requestKey = '', resultLang = lang, freshFromQuiz = false, cacheQuizId = 0 } = options;

      if (!result) return;
      if (requestKey && currentLangRequestRef.current !== requestKey) return;

      const {
        quiz_id,
        ingredients,
        focusArea,
        user: resultUser,
        offer: resultOffer,
        personalSignalsCount: psc,
        specialConditionsCount: scc,
        medicationsCount: mc,
        mainPurpose: mp,
        symptoms: sy,
        priorityFocus: pf,
        isVegan: iv,
        mainPurposeContext: mpc,
        aiFromCache: afc,
      } = result;

      setOmega3Eligibility({
        quizId: Number(quiz_id),
        canSelect: typeof result.canSelectOmega3 === 'boolean' ? result.canSelectOmega3 : undefined,
      });
      if (afc !== undefined) setAiFromCache(freshFromQuiz ? false : afc);

      const normalizedIngredients = withStaticDescriptions(Array.isArray(ingredients) ? ingredients : []);
      const orderedIngredients = orderIngredientsByFocusPriority(normalizedIngredients, pf, { isVegan: !!iv });
      setData(orderedIngredients);
      setIngredients(orderedIngredients);
      setFocusArea(Array.isArray(focusArea) ? focusArea : []);

      if (resultUser?.user) {
        setUser(resultUser.user);
        fetchedNameRef.current = resultUser.user?.name?.trim().split(' ')[0] || null;
      }

      if (quiz_id > 0) {
        setQuizId(quiz_id);
        localStorage.setItem('quiz_id', quiz_id);
        currentLangRequestRef.current = `${quiz_id}:${resultLang}`;
      }

      if (resultOffer !== undefined && resultOffer !== null) {
        setOffer(resultOffer.toString());
      }

      if (resultOffer === 3) {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('offer', '3');
        setSearchParams(nextSearchParams);
      }

      if (psc !== undefined) setPersonalSignalsCount(psc);
      if (scc !== undefined) setSpecialConditionsCount(scc);
      if (mc !== undefined) setMedicationsCount(mc);
      setMainPurpose(mp || '');
      setSymptoms(Array.isArray(sy) ? sy : []);
      setPriorityFocus(Array.isArray(pf) ? pf : []);
      setMainPurposeContext(mpc || null);
      setDataResolved(true);
      setIsFetchingAnswers(false);
      initialLoadDoneRef.current = true;
      sessionStorage.setItem('ps_loaded', '1');

      const normalizedCacheQuizId = Number(cacheQuizId || quiz_id || 0);
      if (normalizedCacheQuizId > 0) {
        setCachedAnswersWebResult(normalizedCacheQuizId, resultLang, result);
      }

      if (result.subscriber?.isRetake === true && quiz_id > 0 && !retakeNavigationStartedRef.current) {
        retakeNavigationStartedRef.current = true;
        const email = String(state?.slideValues?.[12]?.email?.value || resultUser?.user?.email || '').trim();
        if (result.subscriber.isCurrentUser && !result.subscriber.requiresLogin) {
          navigate('/dashboard/comparison', { replace: true });
          return;
        }
        navigate('/login?redirect=/dashboard/comparison', {
          replace: true,
          state: { prefilledEmail: email, activeSubscriberRetake: true },
        });
      }
    },
    [lang, navigate, searchParams, setData, setFocusArea, setIngredients, setQuizId, setSearchParams, setUser, state],
  );

  useEffect(() => {
    if (editIngredientsModal || addIngredientsModal || !omega3.isReady) return;
    preloadCheckoutSessions(checkoutOverrides);
  }, [editIngredientsModal, addIngredientsModal, omega3.isReady, checkoutOverrides, preloadCheckoutSessions]);


  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ALL USE EFFECTS HERE
  useEffect(() => {
    if (subs) {
      const tempSubs = subs.slice(1, subs.length);
      // const sub = [offer && offer === '3' ? THREE_MONTHS_ONE_OFF_SUB : ONE_OFF_SUB, ...tempSubs];
      const sub = [...tempSubs, ONE_OFF_SUB, THREE_MONTHS_ONE_OFF_SUB];
      setNewSubs(sub);
    }
  }, [subs, setNewSubs, ONE_OFF_SUB, THREE_MONTHS_ONE_OFF_SUB, offer]);

  useEffect(() => {
    const receivedData = state;
    if (receivedData && receivedData.quiz_id > 0) {
      setQuizId(receivedData.quiz_id);
      localStorage.setItem('quiz_id', receivedData.quiz_id);
    }
  }, [setQuizId, state]);

  // This will set the links to NO_EU if the country is in the list
  useEffect(() => {
    if (state) {
      let country = '';
      if (state?.slideValues?.[0]?.country?.value) {
        country = state.slideValues[0].country.value;
      } else if (state?.slideValues?.[8]?.country?.value) {
        country = state.slideValues[8].country.value;
      }
      if (country && NON_EU_COUNTRIES.includes(country)) {
        setContinent('non-eu');
      }
    }
  }, [setContinent, state]);

  useEffect(() => {
    const nextLang = selectedLang === 'us' ? 'en' : selectedLang;
    setLang(nextLang);
    if (nextLang) dispatch(getSubsActions(nextLang));
  }, [dispatch, selectedLang, setLang]);

  useEffect(() => {
    if (!lang || freshFromQuizRef.current) return;

    const storedQuizId = stateQuizId > 0 ? String(stateQuizId) : localStorage.getItem('quiz_id');
    const activeQuizId = Number(storedQuizId || quizId || 0);

    if (storedQuizId && String(quizId) !== String(storedQuizId)) {
      setQuizId(storedQuizId);
    }

    if (activeQuizId > 0) {
      const requestKey = `${activeQuizId}:${lang}`;
      if (currentLangRequestRef.current === requestKey) return;

      const requestLang = lang;
      const freshFromQuiz = freshFromQuizRef.current;
      currentLangRequestRef.current = requestKey;

      const cachedResult = getCachedAnswersWebResult(activeQuizId, requestLang);
      if (
        typeof cachedResult?.canSelectOmega3 === 'boolean' &&
        cachedResult?.subscriber &&
        !cachedResult.subscriber.isRetake
      ) {
        applyAnswersWebResult(cachedResult, {
          requestKey,
          resultLang: requestLang,
          freshFromQuiz,
          cacheQuizId: activeQuizId,
        });
        return;
      }

      setIsFetchingAnswers(true);
      loadAnswersWeb({ quiz_id: activeQuizId, answers: null, lang: requestLang, includeAuth: false }, false)
        .unwrap()
        .then((data) =>
          applyAnswersWebResult(data, {
            requestKey,
            resultLang: requestLang,
            freshFromQuiz,
            cacheQuizId: activeQuizId,
          }),
        )
        .catch(() => {
          if (currentLangRequestRef.current === requestKey) {
            currentLangRequestRef.current = null;
            setIsFetchingAnswers(false);
          }
        });
    }
  }, [lang, applyAnswersWebResult, loadAnswersWeb, quizId, setQuizId, stateQuizId]);

  // Fresh quiz: consume prefetch result or fall back to new call
  useEffect(() => {
    const quizState = freshQuizStateRef.current;
    if (!quizState || freshQuizFiredRef.current || !lang) return;
    freshQuizFiredRef.current = true;
    freshQuizStateRef.current = null;

    const handleResult = (data) => {
      applyAnswersWebResult(data, {
        resultLang: lang,
        freshFromQuiz: true,
        cacheQuizId: data?.quiz_id,
      });
      freshFromQuizRef.current = false;
    };

    const prefetchPromise = window.__quizPrefetchPromise;
    if (prefetchPromise) {
      // Reuse the prefetch — no second API call
      window.__quizPrefetchPromise = null;
      prefetchPromise
        .then((res) => handleResult(res?.data || res))
        .catch((e) => {
          console.error('prefetch failed, falling back:', e);
          submitAnswers({
            quiz_id: 0,
            answers: quizState.quizAnswers,
            dataJson: quizState.slideValues,
            slideNavigation: quizState.slideNavigation,
            offer: quizState.offer,
            lang,
            includeAuth: false,
          })
            .unwrap()
            .then(handleResult)
            .catch((e) => console.error('fresh quiz fetch error:', e));
        });
    } else {
      submitAnswers({
        quiz_id: 0,
        answers: quizState.quizAnswers,
        dataJson: quizState.slideValues,
        slideNavigation: quizState.slideNavigation,
        offer: quizState.offer,
        lang,
        includeAuth: false,
      })
        .unwrap()
        .then(handleResult)
        .catch((e) => console.error('fresh quiz fetch error:', e));
    }
  }, [applyAnswersWebResult, lang, submitAnswers]);

  // THis will calculate the initial prices for the ingredients
  useEffect(() => {
    const activeIngredients = Array.isArray(ingredients) ? ingredients : [];
    if (!activeIngredients.length || !allIngredientPrices?.data?.length) return;
    setPrices({});
    activeIngredients.forEach((item) => {
      if (parseFloat(item.daily_dose) > 0) {
        calculateIngredientPrice(
          item,
          parseFloat(item.daily_dose),
          setPrices,
          setPaymentLink,
          setSubscription,
          allIngredientPrices,
          false,
        );
        calculateIngredientPrice(
          item,
          parseFloat(item.daily_dose),
          setPricesData,
          setPaymentLink,
          setSubscription,
          allIngredientPrices,
          false,
        );
      }
    });
  }, [ingredients, allIngredientPrices, setPaymentLink, setPrices, setSubscription, setPricesData]);

  // THis will calculate the price for the product
  useEffect(() => {
    const totalSum = Object.values(prices).reduce((sum, value) => sum + parseFloat(value), 0);
    const shipping = totalSum >= 55 ? 0 : continent === 'eu' ? 6 : 12;

    setTotals({
      oneMonth: totalSum + shipping,
      threeMonths: totalSum * 0.9, // 10% off of 1 month price + shipping
      sixMonths: totalSum * 0.7, // 30% off of 1 month price, free shipping
      threeMonthsOneOff: totalSum * 0.8 * 3 + shipping, // 20% off of 1 month price + shipping
    });
  }, [prices, continent, setTotals]);

  // Ingredients observer
  useEffect(() => {
    if (!mobileSubsRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        const isScrolledPast = !isVisible && entry.boundingClientRect.top < 0;

        setIsPastMobileSubs(isScrolledPast);
      },
      { threshold: 0.1 },
    );

    observer.observe(mobileSubsRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (newSubs.length === 0) return;
    const productPrices = allProductPrices?.data ?? [];
    if (!productPrices.length) return;

    // Ensure totals are calculated (not all zeros)
    const hasValidTotals =
      totals?.oneMonth > 0 ||
      totals?.threeMonths > 0 ||
      totals?.sixMonths > 0 ||
      totals?.threeMonthsOneOff > 0;
    if (!hasValidTotals) return;

    const currentDuration = subscribtion?.duration;
    const currentSub = currentDuration ? newSubs.find((s) => s?.duration === currentDuration) : null;

    const defaultSub = newSubs.find((s) => s?.duration === '3 months');

    const toSelect = currentSub ?? defaultSub;

    if (!toSelect) return;

    const expectedPrice = productPrices.find(
      (price) =>
        hasPaymentLink(price) &&
        price?.product_type === 'riise_formula' &&
        price?.duration === toSelect?.duration &&
        amountMatches(price?.amount, getTargetAmount(toSelect?.duration)),
    );
    const hasCurrentPaymentLink =
      paymentLink && expectedPrice?.payment_link && paymentLink === expectedPrice.payment_link;

    // If the current link still matches the latest calculated total for this plan, don't re-run.
    if (
      hasCurrentPaymentLink &&
      subscribtion?.duration === toSelect?.duration &&
      subscribtion?.subId === toSelect?.id
    ) {
      hasInitializedPlanRef.current = true;
      return;
    }

    // Only initialize once, or re-initialize if paymentLink is missing but we have all data
    // This prevents infinite loops while still fixing the "return to screen" issue
    if (!hasInitializedPlanRef.current || !paymentLink) {
      hasInitializedPlanRef.current = true;
      onSelectPlan(toSelect);
    }
  }, [newSubs, allProductPrices?.data, totals, subscribtion, paymentLink, onSelectPlan]);

  // Handle wheel events on left container to scroll right container
  useEffect(() => {
    const leftContainer = leftContainerRef.current;
    const right = rightScrollRef.current;
    if (!leftContainer || !right) return;

    const handleLeftWheel = (e) => {
      if (window.innerWidth < 1024) return;

      if (!leftContainer.contains(e.target)) {
        return;
      }

      const atTop = right.scrollTop <= 0;
      const atBottom = right.scrollTop + right.clientHeight >= right.scrollHeight - 1;

      if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      right.scrollTop += e.deltaY;
    };

    leftContainer.addEventListener('wheel', handleLeftWheel, { passive: false, capture: true });

    return () => {
      leftContainer.removeEventListener('wheel', handleLeftWheel, { capture: true });
    };
  }, []);

  // Handle wheel events on left/right empty spaces outside main container to scroll right container
  useEffect(() => {
    const mainContainer = mainContainerRef.current;
    const right = rightScrollRef.current;
    if (!mainContainer || !right) return;

    const handleOutsideWheel = (e) => {
      if (window.innerWidth < 1024) return;

      if (mainContainer.contains(e.target)) {
        return;
      }

      const rect = mainContainer.getBoundingClientRect();
      const mouseY = e.clientY;

      if (mouseY < rect.top || mouseY > rect.bottom) {
        return;
      }

      const mouseX = e.clientX;
      const isOutsideContainer = mouseX < rect.left || mouseX > rect.right;

      if (!isOutsideContainer) {
        return;
      }

      const atTop = right.scrollTop <= 0;
      const atBottom = right.scrollTop + right.clientHeight >= right.scrollHeight - 1;

      if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      right.scrollTop += e.deltaY;
    };
    document.addEventListener('wheel', handleOutsideWheel, { passive: false, capture: true });

    return () => {
      document.removeEventListener('wheel', handleOutsideWheel, { capture: true });
    };
  }, []);

  const { metaEvent } = useMeta();

  useEffect(() => {
    metaEvent('ViewContent');
  }, []);

  // Mobile: border when banner scrolls out of viewport
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setScrolledPastBanner(!entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Desktop: border + banner fade when right column scrolls past banner height
  useEffect(() => {
    const el = rightScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const bannerHeight = bannerRef.current?.offsetHeight ?? 0;
      setScrolledPastBanner(el.scrollTop >= bannerHeight);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const getPriceByDuration = (duration, totals, multiplier = 1) => {
    const priceMap = {
      '6 months': totals.sixMonths,
      '3 months': totals.threeMonths,
      '90 days': totals.threeMonthsOneOff,
      '1 month': totals.oneMonth,
      '30 days': totals.oneMonth,
    };
    return (parseFloat(priceMap[duration] || 0) * multiplier).toFixed(2);
  };

  const activePlanPrice = useMemo(() => {
    const duration = subscribtion?.duration;
    if (!duration || !allProductPrices?.data?.length) return null;

    return allProductPrices.data.find(
      (price) =>
        hasPaymentLink(price) &&
        price?.product_type === 'riise_formula' &&
        price?.duration === duration &&
        amountMatches(price?.amount, getTargetAmount(duration)),
    );
  }, [allProductPrices?.data, amountMatches, getTargetAmount, subscribtion?.duration]);

  const activePaymentLink = activePlanPrice?.payment_link === paymentLink ? paymentLink : null;
  const canCheckoutWithoutPaymentLink = omega3.isSelected && Boolean(activePlanPrice);

  const totalPrice = getOmega3CheckoutTotal(
    getPriceByDuration(subscribtion?.duration, totals, subscribtion?.duration === '6 months' ? 3 : 1),
    subscribtion?.duration,
    omega3.isSelected,
  );
  const checkoutMonths = ['6 months', '90 days'].includes(subscribtion?.duration) ? 3 : 1;
  const checkoutStrikeThrough = Math.round(Number(totals.oneMonth || 0) * checkoutMonths) +
    (omega3.isSelected ? getOmega3PlanAmount('30 days') * checkoutMonths : 0);
  const showStrikeThrough = ['6 months', '3 months', '90 days'].includes(subscribtion?.duration);
  const getButtonPriceText = () => {
    if (subscribtion?.duration === '30 days' || subscribtion?.duration === '90 days') return `${omega3.isSelected ? totalPrice.toFixed(2) : totalPrice}€`;
    if (subscribtion?.duration === '6 months') return `${omega3.isSelected ? totalPrice.toFixed(2) : totalPrice}€ ${t(EveryThreeMonths)}`;
    return `${omega3.isSelected ? totalPrice.toFixed(2) : totalPrice}€ /${t(Month)}`;
  };

  useEffect(() => {
    if (!ingredientsModal) return;

    window.history.pushState({ modal: true }, '');

    const handlePopState = () => {
      setIngredientsModal(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [ingredientsModal]);

  return (
    <div
      className="t-relative t-bg-surface t-font-sans t-text-navy"
      style={{ WebkitFontSmoothing: 'antialiased' }}
    >
      <div ref={bannerRef}>{!showFormulaLoader && <DiscountBanner />}</div>
      <Header scrolledPastBanner={scrolledPastBanner} />
      <div
        ref={mainContainerRef}
        className="t-relative t-w-full t-max-w-[1320px] lg:t-h-screen t-mx-auto t-flex t-flex-col md:t-flex-row t-gap-8 lg:t-gap-0 t-py-4 !t-px-4 lg:!t-px-8"
      >
        <div
          ref={leftContainerRef}
          className="lg:t-sticky lg:t-top-0 t-w-full sm:t-max-w-[300px] lg:t-max-w-none lg:t-w-1/2 hide-scrollbar"
          style={{ overflow: 'hidden' }}
        >
          <Carousel
            data={safeData}
            allIngredients={allIngredients}
            onClickViewFormula={() => setIngredientsModal(true)}
            name={user?.name.trim()}
            focusAreaCount={safeFocusArea.length}
            personalSignalsCount={personalSignalsCount}
            specialConditionsCount={specialConditionsCount}
            medicationsCount={medicationsCount}
            suggestion={carouselSuggestion}
          />
        </div>

        <div
          ref={rightScrollRef}
          className="sm:t-w-[calc(100%-300px)] lg:t-w-1/2 lg:t-pl-8 t-flex t-flex-col t-gap-3 lg:t-overflow-y-auto lg:t-h-full hide-scrollbar"
          style={{ overscrollBehavior: 'auto' }}
        >
          <div className="t-flex t-flex-wrap t-items-center t-gap-1 sm:t-gap-2">
            <ReviewsOverlay
              totalReviews={reviewStats?.total ?? 0}
              averageReviews={reviewStats?.average ?? 0}
              onClick={scrollToSectionEndMobile}
            />
            <div className="t-h-4 t-w-[2px] t-bg-navy/30" />
            <p className="t-m-0 t-font-sans t-text-xs sm:t-text-[13px] t-font-semibold t-text-navy/70">
              {t(ProductSuggestionDoses)}
            </p>
          </div>
          <p className="!t-m-0 t-font-sans t-text-xs t-font-bold t-uppercase t-tracking-[0.15em] t-text-[#116D6E]">
            {t(ProductSuggestionEyebrow)}
          </p>
          <h2 className="!t-m-0 t-font-serif t-font-normal t-leading-[0.98] t-text-navy">
            {getPersonalFormulaReadyText(t, user?.name, i18n.language)}
          </h2>
          {mainPurposeSubline && (
            <p className="!t-m-0 t-font-sans t-text-[15px] t-text-navy/70">{mainPurposeSubline}</p>
          )}
          <div className="t-flex t-flex-col t-gap-2">
            <ProductHighlightPill>{t(ProductSuggestionImprovementBadge)}</ProductHighlightPill>
            <ProductHighlightPill>
              {t(ProductSuggestionIngredientsBadge, { ingredientsCount: totalIngredientsCount })}
            </ProductHighlightPill>
          </div>
          <p className="!t-m-0 t-font-sans t-text-[15px] t-text-navy/70">{t(YourFocusAreasDesc)}</p>
          <FocusAreaAccordion
            focusArea={orderedFocusArea}
            focusAreaIcons={focusAreaIcons}
            collapsedCount={priorityFocusCollapsedCount}
          />
          <p className="!t-m-0 t-text-center md:t-text-left t-italic t-font-sans t-text-sm t-text-g500">
            {t(VeganVCP)}
          </p>
          <Omega3ProductSelector
            t={t}
            hasFormula={safeData.length > 0}
            hasOmega3={omega3.hasAdded}
            hasOmega3Selected={omega3.isSelected}
            onOmega3Change={omega3.handleChange}
            omega3Price={omega3PriceLabel}
            titleKey="Omega3_Selection_Title"
            descriptionKey="Omega3_Selection_Description"
            packagingKey="Omega3_Selection_Packaging"
            showSelectionLabel
          />
          <div ref={mobileSubsRef} className="!md:t-hidden">
            {offer === '3' && !omega3.isSelected ? (
              <MobileSubscriptionCardV2
                username={user?.name.trim()}
                subscribtion={subscribtion}
                subs={newSubs}
                onSelectPlan={onSelectPlan}
                totals={totals}
                paymentLink={activePaymentLink}
                isCheckoutLoading={isCheckoutLoading}
                onSubmitData={handleCheckout}
                onSelectOneOff={onSelectOneOff}
                error={error}
                boxRef={boxRef}
                focusArea={safeFocusArea}
                totalIngredients={totalIngredientsCount}
              />
            ) : (
              <MobileSubscriptionCardV4
                showOmega3Bottle={omega3.isSelected}
                showOmega3Benefit={omega3.isSelected}
                omega3BenefitKey="Omega3_Selection_Title"
                ctaPriceTextOverride={omega3.isSelected ? getButtonPriceText() : null}
                ctaStrikeThroughPriceOverride={omega3.isSelected ? checkoutStrikeThrough : null}
                canCheckoutWithoutPaymentLink={canCheckoutWithoutPaymentLink}
                username={user?.name.trim()}
                subscribtion={subscribtion}
                subs={newSubs}
                onSelectPlan={onSelectPlan}
                totals={totals}
                paymentLink={activePaymentLink}
                isCheckoutLoading={isCheckoutLoading}
                onSubmitData={handleCheckout}
                onSelectOneOff={onSelectOneOff}
                error={error}
                boxRef={boxRef}
                focusArea={safeFocusArea}
                totalIngredients={totalIngredientsCount}
                ingredients={safeData}
                proofTitleClassName="t-text-left t-text-2xl"
                testimonialsSlot={<PdpTestimonials titleClassName="t-text-left t-text-2xl" />}
              />
            )}
          </div>
        </div>
      </div>
      <ProductPageSections
        lang={lang}
        rating={reviewStats?.average || '4.88'}
        reviewsRef={sectionRefMobile}
      />
      <div
        className="md:t-hidden t-fixed t-bottom-0 t-left-0 t-right-0 t-bg-surface/[.96] t-backdrop-blur-[10px] t-border-t t-border-g200 t-py-3.5 t-px-[18px] t-flex t-justify-center t-z-40"
        style={{ paddingBottom: 'max(14px, env(safe-area-inset-bottom))' }}
        hidden={!isBannerVisible || !safeData.length}
      >
        <button
          className={`t-w-full t-max-w-[520px] t-py-[15px] t-px-6 t-rounded-full t-bg-quaternary t-text-white t-font-sans t-text-[16px] t-font-bold ${isGermanLanguage(i18n.language) ? '' : 't-uppercase'} t-tracking-[0.5px] t-border-0 t-cursor-pointer t-outline-none hover:t-bg-[#2da898] disabled:t-opacity-30 disabled:t-cursor-not-allowed`}
          onClick={() => handleCheckout(activePaymentLink)}
          disabled={
            isCheckoutLoading ||
            (!activePaymentLink && !canCheckoutWithoutPaymentLink) ||
            Object.values(error).some((value) => value === true)
          }
        >
          {t(StartNowButton)} -{' '}
          {showStrikeThrough && (
            <span className="t-line-through t-opacity-60">
              {checkoutStrikeThrough}
              €
            </span>
          )}{' '}
          <span className="t-font-bold">{getButtonPriceText()}</span>
        </button>
      </div>

      <IngredientsModal
        open={ingredientsModal}
        handleClose={() => setIngredientsModal(false)}
        data={safeData}
        allIngredients={allIngredients}
        name={user?.name.trim()}
        focusAreaCount={safeFocusArea.length}
        personalSignalsCount={personalSignalsCount}
        specialConditionsCount={specialConditionsCount}
        medicationsCount={medicationsCount}
        onOpenEdit={() => {
          setIngredientsModal(false);
          setEditIngredientsModal(true);
        }}
      />
      <EditFormulaModal
        open={editIngredientsModal}
        handleClose={() => {
          setEditIngredientsModal(false);
          if (editOpenedFromCarouselRef.current) editOpenedFromCarouselRef.current = false;
          else setIngredientsModal(true);
          if (hasFormulaChanged) preloadCheckoutSessions(checkoutOverrides);
        }}
        data={safeData}
        ingredients={safeIngredients}
        hasFormulaChanged={hasFormulaChanged || omega3.isSelected}
        setAddIngredientsModal={setAddIngredientsModal}
        allIngredients={allIngredients}
        onRemoveQuantity={onRemoveQuantity}
        onAddQuantity={onAddQuantity}
        prices={prices}
        removeIngredientFromBin={removeIngredientFromBin}
        onInputChange={onInputChange}
        omega3Option={omega3Option}
        onReset={handleResetFormula}
        name={user?.name.trim()}
      />
      <AddIngredientModal
        open={addIngredientsModal}
        handleClose={() => setAddIngredientsModal(false)}
        omega3Option={omega3Option}
        data={safeData}
        removeIngredient={removeIngredient}
        addIngredient={addIngredient}
        allIngredients={allIngredients}
      />
      <a
        href="https://wa.me/+4915156047816"
        target="_blank"
        rel="noreferrer"
        className="t-fixed t-bottom-28 md:t-bottom-5 t-right-7 t-z-50 t-bg-white t-rounded-full t-shadow-md"
        onClick={() => metaEvent('Contact')}
      >
        <img src={WhatsappImg} alt="" className="t-h-10 t-w-10 md:t-h-15 md:t-w-15" />
      </a>
      {showFormulaLoader && (
        <FormulaLoader
          fromCache={returningUserRef.current || aiFromCache}
          dataResolved={dataResolved}
          name={
            freshNameRef.current ||
            fetchedNameRef.current ||
            reduxUser?.name?.trim().split(' ')[0] ||
            user?.name?.trim().split(' ')[0]
          }
          skipPending={freshFromQuizRef.current}
          onComplete={() => {
            setShowFormulaLoader(false);
          }}
        />
      )}
      {isResultLoading && !showFormulaLoader && <Loader />}
    </div>
  );
};

export default ProductSuggestion;
